import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  OnInit,
  OnDestroy,
  HostListener,
  computed,
  inject,
  signal,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  forwardRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { PdfViewerComponent } from '../project-annotator/pdf-viewer.component';

import { Project } from '../../../dtos/project';
import { Group } from '../../../dtos/group';
import { ProjectService } from '../../../services/project.service';
import { GroupService } from '../../../services/group.service';
import { ProjectState } from '../../../dtos/project-state';
import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { AgreementStatsCardComponent } from '../agreement-stats-card/agreement-stats-card.component';
import { ScreeningCriteriaBarComponent } from '../screening-criteria-bar/screening-criteria-bar.component';
import { ScreeningProgressCardComponent } from '../screening-progress-card/screening-progress-card.component';

import { PDF_SOURCE } from '../../../claim-annotator/ports/pdf-source.port';
import { InMemoryPdfSource } from '../../../claim-annotator/adapters/in-memory-pdf-source';
import { ANNOTATION_DRAFT_CACHE } from '../../../claim-annotator/ports/annotation-draft-cache.port';
import { LocalStorageDraftCache } from '../../../claim-annotator/adapters/local-storage-draft-cache';

import { AnnotationStore } from '../../../claim-annotator/domain/annotation-store';
import { AnnotationWorkflow } from '../../../claim-annotator/domain/annotation-workflow';
import { ClaimSetState } from '../../../claim-annotator/domain/claim-set-state';
import { CLAIMS_PROVIDER } from '../../../claim-annotator/ports/claims-provider.port';
import { ANNOTATION_REPOSITORY } from '../../../claim-annotator/ports/annotation-repository.port';
import { USER_CONTEXT } from '../../../claim-annotator/ports/user-context.port';
import { HttpAnnotationsAdapter } from '../../../claim-annotator/adapters/http-annotations';
import { AuthUserContext } from '../../../claim-annotator/adapters/auth-user-context';
import { QuestionnaireClaimsAdapter } from '../../../claim-annotator/adapters/questionnaire-claims.adapter';
import { QUESTIONNAIRE_PORT } from '../../../extraction-schema/ports/questionnaire.port';
import { HttpQuestionnaireAdapter } from '../../../extraction-schema/adapters/http-questionnaire.adapter';

import { HttpPdfCorpus } from '../../../pdf-corpus/adapters/http-pdf-corpus';
import { PDF_CORPUS_PORT } from '../../../pdf-corpus/ports/pdf-corpus.port';
import { PdfCorpusService } from '../../../pdf-corpus/domain/pdf-corpus.service';

import { InMemoryProjectCorpus } from '../../../project-corpus/adapters/in-memory-project-corpus';
import { HybridProjectCorpus } from '../../../project-corpus/adapters/hybrid-project-corpus';
import { PROJECT_CORPUS_PORT } from '../../../project-corpus/ports/project-corpus.port';

import { httpErrorToMessage } from '../../../project-corpus/adapters/http-error-message';

import {
  SecondStageScreeningService,
  SecondStagePaper,
  SecondStageScreeningStats,
} from '../../../services/second-stage-screening.service';

@Component({
  selector: 'app-project-full-text-screening',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    PdfViewerComponent,
    BaseAlertComponent,
    ConfirmModalComponent,
    ScreeningCriteriaBarComponent,
    ScreeningProgressCardComponent,
    forwardRef(() => PiToolbarActionsComponent),
    AgreementStatsCardComponent,
  ],
  providers: [
    InMemoryPdfSource,
    QuestionnaireClaimsAdapter,
    HttpAnnotationsAdapter,
    LocalStorageDraftCache,
    AuthUserContext,
    { provide: PDF_SOURCE, useExisting: InMemoryPdfSource },
    { provide: QUESTIONNAIRE_PORT, useClass: HttpQuestionnaireAdapter },
    { provide: CLAIMS_PROVIDER, useExisting: QuestionnaireClaimsAdapter },
    { provide: ANNOTATION_REPOSITORY, useClass: HttpAnnotationsAdapter },
    { provide: ANNOTATION_DRAFT_CACHE, useClass: LocalStorageDraftCache },
    { provide: USER_CONTEXT, useExisting: AuthUserContext },
    ClaimSetState,
    AnnotationStore,
    AnnotationWorkflow,
    HttpPdfCorpus,
    { provide: PDF_CORPUS_PORT, useExisting: HttpPdfCorpus },
    PdfCorpusService,
    InMemoryProjectCorpus,
    HybridProjectCorpus,
    { provide: PROJECT_CORPUS_PORT, useExisting: HybridProjectCorpus },
  ],
  templateUrl: './project-full-text-screening.component.html',
})
export class ProjectFullTextScreeningComponent implements OnInit, OnDestroy {
  protected store = inject(AnnotationStore);
  private pdfSource = inject(InMemoryPdfSource);
  private pdfCorpus = inject(PdfCorpusService);
  private projectCorpus = inject(PROJECT_CORPUS_PORT);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  private projectService = inject(ProjectService);
  private groupService = inject(GroupService);
  private router = inject(Router);
  private secondStageScreeningService = inject(SecondStageScreeningService);

  project: Project | null = null;
  group: Group | null = null;
  readonly ProjectState = ProjectState;

  readonly submitting = signal<boolean>(false);
  readonly lastError = signal<string | null>(null);
  readonly advanceConfirmOpen = signal(false);
  readonly memberOnlyNotice = signal<string | null>(null);
  readonly pendingVotesDismissed = signal(false);
  readonly unresolvedConflictsDismissed = signal(false);

  readonly projectId = signal<string>('');
  readonly groupId = signal<string | null>(null);

  readonly selectedPaperId = signal<string | null>(null);
  currentSourceKind: 'file' | 'url' = 'file';

  readonly stats = signal<SecondStageScreeningStats>({
    totalPapers: 0,
    unscreenedPapers: 0,
    partiallyScreenedPapers: 0,
    includedPapers: 0,
    excludedPapers: 0,
  });
  readonly unresolvedConflictCount = signal(0);
  readonly canAdvanceToDataExtraction = computed(() => {
    const s = this.stats();
    return (
      s.unscreenedPapers === 0 &&
      s.partiallyScreenedPapers === 0 &&
      this.unresolvedConflictCount() === 0
    );
  });
  readonly hasPendingScreeningVotes = computed(() => {
    const s = this.stats();
    return s.unscreenedPapers > 0 || s.partiallyScreenedPapers > 0;
  });
  readonly hasUnresolvedConflicts = computed(() => this.unresolvedConflictCount() > 0);
  readonly exclusionReasons = signal<string[]>([]);
  readonly showExclusionPicker = signal<boolean>(false);
  readonly activeScreeningPaper = signal<SecondStagePaper | null>(null);
  readonly votedPapersStack = signal<SecondStagePaper[]>([]);
  readonly paperLocked = signal(true);
  readonly hasRequestedFirstPaper = signal(false);

  get isPI(): boolean {
    return this.project?.userRole === 'PI';
  }

  get isProjectReadOnly(): boolean {
    return (
      this.project?.state === ProjectState.DATA_EXTRACTION ||
      this.project?.state === ProjectState.COMPLETED
    );
  }

  get currentProjectState(): ProjectState | string | null {
    return this.project?.state ?? null;
  }

  ngOnInit(): void {
    const id = this.route.parent?.snapshot.paramMap.get('projectId') ?? '';
    this.projectId.set(id);
    if (!id) return;

    this.projectService
      .getProjectById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (proj) => {
          this.project = proj;
          if (proj) {
            this.groupService
              .getGroupById(proj.groupId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (grp) => (this.group = grp),
                error: (err) => console.error('Failed to load group details', err),
              });
          }

          if (proj?.userRole === 'NONE') {
            this.memberOnlyNotice.set(
              'This page is available to project members only. You do not have access to full-text screening for this project.',
            );
            return;
          }

          this.projectCorpus
            .getProjectSummary(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (s) => {
                this.groupId.set(s.groupId);
                this.loadExclusionReasons();
                this.loadStats();
                this.loadConflicts();
                this.resumeCurrentOrWait();
              },
              error: () => this.lastError.set('Failed to load the project summary.'),
            });
        },
        error: () => this.lastError.set('Failed to load the project.'),
      });
  }

  loadExclusionReasons(): void {
    this.projectService
      .getExclusionReasons(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (reasons) => this.exclusionReasons.set(reasons),
        error: () => this.lastError.set('Failed to load exclusion reasons.'),
      });
  }

  loadStats(): void {
    this.secondStageScreeningService
      .getStats(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => {
          this.stats.set(s);
          if (s.unscreenedPapers === 0) {
            this.hasRequestedFirstPaper.set(true);
          }
        },
        error: () => this.lastError.set('Failed to load screening statistics.'),
      });
  }

  loadConflicts(): void {
    if (!this.isPI || this.isProjectReadOnly) {
      this.unresolvedConflictCount.set(0);
      return;
    }
    this.secondStageScreeningService
      .getConflicts(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (conflicts) => this.unresolvedConflictCount.set(conflicts.length),
        error: () => this.lastError.set('Failed to load screening conflicts.'),
      });
  }

  /**
   * On init, check if the user already holds a lock (e.g. after a page reload) and restore it.
   * If no lock is held, show the "Start Screening" button instead of auto-locking.
   */
  private resumeCurrentOrWait(): void {
    if (this.isProjectReadOnly) return;
    this.secondStageScreeningService
      .getCurrentPaper(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (paper) => {
          if (paper) {
            this.activeScreeningPaper.set(paper);
            this.paperLocked.set(true);
            this.hasRequestedFirstPaper.set(true);
            this.loadScreeningPaper(paper);
          }
        },
        error: () => {
          // Ignore — just show the start button
        },
      });
  }

  loadNextScreeningPaper(): void {
    this.hasRequestedFirstPaper.set(true);
    if (this.isProjectReadOnly) {
      this.activeScreeningPaper.set(null);
      this.selectedPaperId.set(null);
      return;
    }

    this.secondStageScreeningService
      .getNextPaper(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (paper) => {
          if (paper) {
            this.activeScreeningPaper.set(paper);
            this.paperLocked.set(true);
            this.loadScreeningPaper(paper);
          } else {
            this.activeScreeningPaper.set(null);
            this.selectedPaperId.set(null);
            this.paperLocked.set(false);
          }
        },
        error: (err) => {
          if (err instanceof HttpErrorResponse && err.status === 409) {
            this.activeScreeningPaper.set(null);
            this.selectedPaperId.set(null);
            this.paperLocked.set(false);
            this.projectService
              .getProjectById(this.projectId())
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (proj) => {
                  this.project = proj;
                },
                error: (e) => console.error('Failed to reload project on 409', e),
              });
          } else {
            this.lastError.set('Failed to load the next paper in the queue.');
          }
        },
      });
  }

  async loadScreeningPaper(paper: SecondStagePaper): Promise<void> {
    this.lastError.set(null);
    this.selectedPaperId.set(paper.id.toString());
    const docKey = `paper:${paper.id}`;
    try {
      if (paper.pdfId != null) {
        const gid = this.groupId();
        if (gid == null) {
          this.lastError.set('Project group is still loading; try again in a moment.');
          return;
        }
        this.currentSourceKind = 'file';
        const blob = await firstValueFrom(
          this.pdfCorpus.fetchBytes(Number(gid), Number(paper.pdfId)),
        );
        this.openPaperDocument(docKey, 'file', paper.title || paper.id.toString());
        this.pdfSource.setBlob(blob, paper.title || `${paper.id}.pdf`);
      } else if (paper.pdfUrl != null) {
        // Never hand a cross-origin URL to the PDF viewer (the browser's pdf.js fetch is
        // CORS-blocked). Materialize it on the backend (server-side download, idempotent),
        // then load the stored bytes same-origin as a blob — same path as data extraction.
        const gid = this.groupId();
        if (gid == null) {
          this.lastError.set('Project group is still loading; try again in a moment.');
          return;
        }
        this.currentSourceKind = 'url';
        const pdfId = await firstValueFrom(
          this.pdfCorpus.materializePdf(Number(gid), paper.paperId),
        );
        const blob = await firstValueFrom(this.pdfCorpus.fetchBytes(Number(gid), pdfId));
        this.openPaperDocument(docKey, 'file', paper.title || paper.id.toString());
        this.pdfSource.setBlob(blob, paper.title || `${paper.id}.pdf`);
      } else {
        this.lastError.set('This paper has no PDF to screen.');
        return;
      }
    } catch (e) {
      const base =
        this.currentSourceKind === 'url'
          ? "Couldn't load the PDF from its external link; the source may block direct access."
          : 'Failed to load the PDF for this paper.';
      const detail = (e as Error).message ? ' ' + (e as Error).message : '';
      this.lastError.set(base + detail);
    }
  }

  private openPaperDocument(docKey: string, kind: 'file' | 'url', ref: string): void {
    this.store.openDocument({
      source_kind: kind,
      source_ref: ref,
      content_hash: docKey,
      page_count: 0,
    });
  }

  async voteInclude(): Promise<void> {
    const paper = this.activeScreeningPaper();
    if (!paper) return;
    if (this.submitting()) return;
    this.lastError.set(null);
    this.submitting.set(true);
    try {
      await firstValueFrom(
        this.secondStageScreeningService.submitVote(this.projectId(), paper.id, true),
      );
      this.votedPapersStack.update((stack) => {
        if (stack.length > 0 && stack[stack.length - 1].id === paper.id) return stack;
        return [...stack, paper];
      });
      this.activeScreeningPaper.set(null);
      this.store.setActiveDocument(null);
      this.pdfSource.clear();
      this.loadStats();
      this.loadConflicts();
      this.loadNextScreeningPaper();
    } catch (e) {
      this.lastError.set(this.voteErrorMessage(e));
    } finally {
      this.submitting.set(false);
    }
  }

  openExclusionPicker(): void {
    if (this.exclusionReasons().length === 0) {
      this.lastError.set(
        'No active exclusion reasons configured for this project. Please configure them on the Project Details page.',
      );
      return;
    }
    this.showExclusionPicker.set(true);
  }

  async voteExclude(reason: string): Promise<void> {
    const paper = this.activeScreeningPaper();
    if (!paper) return;
    if (this.submitting()) return;
    this.lastError.set(null);
    this.submitting.set(true);
    try {
      await firstValueFrom(
        this.secondStageScreeningService.submitVote(this.projectId(), paper.id, false, reason),
      );
      this.votedPapersStack.update((stack) => {
        if (stack.length > 0 && stack[stack.length - 1].id === paper.id) return stack;
        return [...stack, paper];
      });
      this.showExclusionPicker.set(false);
      this.activeScreeningPaper.set(null);
      this.store.setActiveDocument(null);
      this.pdfSource.clear();
      this.loadStats();
      this.loadConflicts();
      this.loadNextScreeningPaper();
    } catch (e) {
      this.lastError.set(this.voteErrorMessage(e));
    } finally {
      this.submitting.set(false);
    }
  }

  async skipPaper(): Promise<void> {
    const paper = this.activeScreeningPaper();
    if (!paper) return;
    if (this.submitting()) return;
    this.lastError.set(null);
    this.submitting.set(true);
    try {
      await firstValueFrom(this.secondStageScreeningService.skipPaper(this.projectId(), paper.id));
      this.votedPapersStack.update((stack) => {
        if (stack.length > 0 && stack[stack.length - 1].id === paper.id) return stack;
        return [...stack, paper];
      });
      this.activeScreeningPaper.set(null);
      this.store.setActiveDocument(null);
      this.pdfSource.clear();
      this.loadStats();
      this.loadConflicts();
      this.loadNextScreeningPaper();
    } catch (e) {
      this.lastError.set(this.voteErrorMessage(e));
    } finally {
      this.submitting.set(false);
    }
  }

  async unlockCurrentPaper(): Promise<void> {
    const paper = this.activeScreeningPaper();
    if (!paper) return;
    if (this.submitting()) return;
    this.lastError.set(null);
    this.submitting.set(true);
    try {
      await firstValueFrom(
        this.secondStageScreeningService.unlockPaper(this.projectId(), paper.id),
      );
      this.router.navigate(['/projects', this.projectId()]);
    } catch (e) {
      this.router.navigate(['/projects', this.projectId()]);
    }
  }

  async undoLastVote(): Promise<void> {
    const stack = this.votedPapersStack();
    if (stack.length === 0) return;
    if (this.submitting()) return;
    const paper = stack[stack.length - 1];

    this.lastError.set(null);
    this.submitting.set(true);
    try {
      const activePaper = this.activeScreeningPaper();
      if (activePaper && activePaper.id !== paper.id) {
        await firstValueFrom(
          this.secondStageScreeningService.unlockPaper(this.projectId(), activePaper.id),
        );
      }
      await firstValueFrom(this.secondStageScreeningService.deleteVote(this.projectId(), paper.id));
      this.votedPapersStack.update((s) => s.slice(0, -1));
      this.activeScreeningPaper.set(paper);
      await this.loadScreeningPaper(paper);
      this.loadStats();
      this.loadConflicts();
    } catch (e) {
      this.lastError.set(this.voteErrorMessage(e));
    } finally {
      this.submitting.set(false);
    }
  }

  onAdvanceClick(): void {
    if (!this.isPI) return;
    if (!this.canAdvanceToDataExtraction()) return;
    this.advanceConfirmOpen.set(true);
  }

  navigateToConflictResolution(): void {
    this.router.navigate(['/projects', this.projectId(), 'conflict-resolution']);
  }

  onAdvanceConfirmOpenChange(open: boolean): void {
    this.advanceConfirmOpen.set(open);
  }

  async advanceProject(): Promise<void> {
    if (!this.isPI) return;
    if (!this.canAdvanceToDataExtraction()) {
      this.advanceConfirmOpen.set(false);
      return;
    }
    this.lastError.set(null);
    this.submitting.set(true);
    try {
      await firstValueFrom(
        this.secondStageScreeningService.advanceToDataExtraction(this.projectId()),
      );
      this.advanceConfirmOpen.set(false);
      this.projectService.notifyProjectUpdated();
      this.router.navigate(['/projects', this.projectId(), 'data-extraction']);
    } catch (e) {
      this.advanceConfirmOpen.set(false);
      this.lastError.set(this.voteErrorMessage(e));
    } finally {
      this.submitting.set(false);
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.isProjectReadOnly || this.submitting() || this.showExclusionPicker()) return;
    // Never hijack browser/OS shortcuts (e.g. Ctrl/Cmd+S used to "save" must not skip a paper).
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const activeEl = document.activeElement;
    if (
      activeEl &&
      (activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.tagName === 'SELECT' ||
        activeEl.hasAttribute('contenteditable'))
    ) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === 'i') {
      event.preventDefault();
      this.voteInclude();
    } else if (key === 'e') {
      event.preventDefault();
      this.openExclusionPicker();
    } else if (key === 's') {
      event.preventDefault();
      this.skipPaper();
    } else if (key === 'u') {
      event.preventDefault();
      this.undoLastVote();
    }
  }

  onViewerLoadFailed(): void {
    this.lastError.set('The PDF failed to load in the viewer.');
  }

  ngOnDestroy(): void {}

  private voteErrorMessage(e: unknown): string {
    if (e instanceof HttpErrorResponse && e.status === 401) {
      this.router.navigate(['/login']);
      return 'Your session has expired. Please log in again.';
    }
    return httpErrorToMessage(e);
  }
}

@Component({
  selector: 'app-pi-toolbar-actions',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="flex items-center gap-2">
      <button
        class="px-4 py-1.5 rounded-lg border font-semibold text-amber-700 bg-amber-50 border-amber-400 hover:bg-amber-100 transition-colors shadow-sm inline-flex items-center gap-1.5 cursor-pointer"
        type="button"
        (click)="navigate.emit()"
        data-testid="resolve-conflicts-btn"
      >
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
        Resolve Conflicts
        @if (unresolvedCount > 0) {
          <span
            class="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold bg-red-100 text-red-700 rounded-full"
          >
            {{ unresolvedCount }}
          </span>
        }
      </button>
      @if (showAdvance) {
        <button
          class="px-4 py-1.5 rounded-lg border font-semibold text-white bg-blue-600 border-blue-700 hover:bg-blue-700 hover:border-blue-800 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:hidden"
          [disabled]="advanceDisabled"
          (click)="advance.emit()"
          data-testid="advance-to-extraction-btn"
          title="Available once all full-text screening votes and conflicts are complete"
        >
          Advance to Data Extraction
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M13 5l7 7-7 7M5 5l7 7-7 7"
            />
          </svg>
        </button>
      }
    </div>
  `,
})
export class PiToolbarActionsComponent {
  @Input({ required: true }) unresolvedCount!: number;
  @Input() showAdvance = true;
  @Input() advanceDisabled = false;
  @Output() navigate = new EventEmitter<void>();
  @Output() advance = new EventEmitter<void>();
}
